import { baseApi } from '@klp/core/api/services/rtkApi';
import { generatePath } from '@klp/routing';

import type { FileObject, IFileObject } from './classes';
import { FileObject as FileObjectClass } from './classes';

export const fileRtkApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getFileByPath: builder.query<FileObject, FileObject>({
            query: (file) => ({
                url: file.filePath || '',
                params: file.imageParams,
            }),
            transformResponse: (
                response: Record<string, unknown>,
                _meta,
                file,
            ) => {
                // Merge response data with original file object
                const updatedData = { ...file };
                const originalFilePath = updatedData.filePath;
                const originalType = updatedData.type;

                for (const key in response) {
                    if (key !== 'filePath') {
                        updatedData[key] = response[key];
                    }
                }

                updatedData.filePath = originalFilePath;
                updatedData.type = originalType;

                return new FileObjectClass({ ...updatedData });
            },
            providesTags: (result, error, file) => [
                { type: 'File', id: file.id },
            ],
        }),
        deleteFile: builder.mutation<void, { file: FileObject; path: string }>({
            query: ({ file, path }) => ({
                url: generatePath(path, file.id),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { file }) => [
                { type: 'File', id: file.id },
            ],
        }),
        deleteFiles: builder.mutation<
            void,
            { files: Array<FileObject>; path: string }
        >({
            queryFn: async (
                { files, path },
                _api,
                _extraOptions,
                baseQuery,
            ) => {
                try {
                    await Promise.all(
                        files.map((file) =>
                            baseQuery({
                                url: generatePath(path, file.id),
                                method: 'DELETE',
                            }),
                        ),
                    );
                    return { data: undefined };
                } catch (error) {
                    return { error };
                }
            },
            invalidatesTags: (result, error, { files }) =>
                files.map((file) => ({ type: 'File' as const, id: file.id })),
        }),
        uploadFile: builder.mutation<
            FileObject,
            { file: FileObject; path?: string }
        >({
            query: ({ file, path }) => ({
                url: path || '',
                method: 'POST',
                body: {
                    filePath: file.fileName,
                    fileContent: file.fileContent?.split(',')[1],
                    type: file.type,
                },
            }),
            transformResponse: (response: Record<string, unknown>) =>
                new FileObjectClass(response as unknown as IFileObject),
            invalidatesTags: (result) =>
                result ? [{ type: 'File' as const, id: result.id }] : [],
        }),
        uploadFiles: builder.mutation<
            Array<FileObject>,
            { files: Array<FileObject>; path?: string }
        >({
            queryFn: async (
                { files, path },
                _api,
                _extraOptions,
                baseQuery,
            ) => {
                try {
                    const uploadedFiles = await Promise.all(
                        files.map(async (file) => {
                            const result = await baseQuery({
                                url: path || '',
                                method: 'POST',
                                body: {
                                    filePath: file.fileName,
                                    fileContent:
                                        file.fileContent?.split(',')[1],
                                    type: file.type,
                                },
                            });

                            if (result.error) {
                                throw result.error;
                            }

                            return new FileObjectClass(
                                result.data as unknown as IFileObject,
                            );
                        }),
                    );
                    return { data: uploadedFiles };
                } catch (error) {
                    return { error };
                }
            },
            invalidatesTags: (result) =>
                result
                    ? result.map((file) => ({
                          type: 'File' as const,
                          id: file.id,
                      }))
                    : [],
        }),
    }),
});

export const {
    useGetFileByPathQuery,
    useLazyGetFileByPathQuery,
    useDeleteFileMutation,
    useDeleteFilesMutation,
    useUploadFileMutation,
    useUploadFilesMutation,
} = fileRtkApi;
